import React, { useState, useEffect } from 'react';
import { PermissionService } from '../Services/PermissionService.js';
import close from '../../../assets/icons/close.svg';
import Select from 'react-select';

const CreatePermissionModal = ({ isOpen, onClose, onSave, moduleTree }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    module: null,
    submodule: null,
    status: true
  });
  const [modules, setModules] = useState([]);
  const [submodules, setSubmodules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (moduleTree) {
      const moduleOptions = moduleTree.map(module => ({
        value: module.id,
        label: module.name
      }));
      setModules(moduleOptions);
    }
  }, [moduleTree]);

  const handleModuleChange = (selectedModule) => {
    setFormData({
      ...formData,
      module: selectedModule,
      submodule: null
    });

    // Find submodules for selected module
    const selectedModuleData = moduleTree.find(m => m.id === selectedModule?.value);
    if (selectedModuleData?.submodules) {
      const submoduleOptions = selectedModuleData.submodules.map(sub => ({
        value: sub.id,
        label: sub.name
      }));
      setSubmodules(submoduleOptions);
    } else {
      setSubmodules([]);
    }
    
    // Clear errors
    setErrors(prev => ({ ...prev, module: '', submodule: '' }));
  };

  const handleSubmoduleChange = (selectedSubmodule) => {
    setFormData({
      ...formData,
      submodule: selectedSubmodule
    });
    setErrors(prev => ({ ...prev, submodule: '' }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Auto-generate code from name
    if (name === 'name' && value) {
      const generatedCode = value
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
      setFormData(prev => ({
        ...prev,
        name: value,
        code: generatedCode
      }));
    }
    
    // Clear errors
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Permission name is required';
    }
    
    if (!formData.code.trim()) {
      newErrors.code = 'Permission code is required';
    }
    
    if (!formData.module) {
      newErrors.module = 'Module selection is required';
    }
    
    if (!formData.submodule) {
      newErrors.submodule = 'Submodule selection is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        module: formData.module.value,
        submodule: formData.submodule.value,
        status: formData.status
      };
      
      await onSave(submitData);
      handleReset();
    } catch (error) {
      console.error('Error saving permission:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      code: '',
      module: null,
      submodule: null,
      status: true
    });
    setSubmodules([]);
    setErrors({});
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: '500px', maxWidth: '90%' }}>
        <div className="modal-header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 className="text-primary" style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#222F3E' }}>
            Create New Permission
          </h2>
          <button className="close-btn" onClick={handleCancel} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <img src={close} alt="close" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-12">
              <label className="form-labell">Permission Name <span className="astrisk">*</span></label>
              <input
                type="text"
                className="fm-modal form-control"
                placeholder="e.g., Add Payment"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={{ borderColor: errors.name ? '#FF0000' : '' }}
              />
              {errors.name && <span style={{ color: '#FF0000', fontSize: '12px' }}>{errors.name}</span>}
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <label className="form-labell">Permission Code <span className="astrisk">*</span></label>
              <input
                type="text"
                className="fm-modal form-control"
                placeholder="e.g., add_payment"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                style={{ borderColor: errors.code ? '#FF0000' : '' }}
              />
              {errors.code && <span style={{ color: '#FF0000', fontSize: '12px' }}>{errors.code}</span>}
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <label className="form-labell">Module <span className="astrisk">*</span></label>
              <Select
                options={modules}
                value={formData.module}
                onChange={handleModuleChange}
                placeholder="Select Module"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({ 
                    ...base, 
                    minHeight: '50px', 
                    height: '50px',
                    borderColor: errors.module ? '#FF0000' : base.borderColor
                  }),
                  valueContainer: (base) => ({ ...base, height: '50px', padding: '0 6px', fontSize: '15px' }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '15px',
                    backgroundColor: state.isSelected ? '#7F56DA' : state.isFocused ? '#F2EDFF' : base.backgroundColor,
                    color: state.isSelected ? 'white' : '#222F3E',
                  }),
                }}
              />
              {errors.module && <span style={{ color: '#FF0000', fontSize: '12px' }}>{errors.module}</span>}
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <label className="form-labell">Submodule <span className="astrisk">*</span></label>
              <Select
                options={submodules}
                value={formData.submodule}
                onChange={handleSubmoduleChange}
                placeholder="Select Submodule"
                isDisabled={!formData.module}
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({ 
                    ...base, 
                    minHeight: '50px', 
                    height: '50px',
                    borderColor: errors.submodule ? '#FF0000' : base.borderColor
                  }),
                  valueContainer: (base) => ({ ...base, height: '50px', padding: '0 6px', fontSize: '15px' }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '15px',
                    backgroundColor: state.isSelected ? '#7F56DA' : state.isFocused ? '#F2EDFF' : base.backgroundColor,
                    color: state.isSelected ? 'white' : '#222F3E',
                  }),
                }}
              />
              {errors.submodule && <span style={{ color: '#FF0000', fontSize: '12px' }}>{errors.submodule}</span>}
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                <input
                  type="checkbox"
                  id="status"
                  name="status"
                  checked={formData.status}
                  onChange={handleInputChange}
                  style={{ accentColor: '#7F56DA' }}
                />
                <label htmlFor="status" style={{ fontSize: '14px', color: '#222F3E', cursor: 'pointer' }}>
                  Active Status
                </label>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                backgroundColor: '#ccc',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                border: 'none',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                backgroundColor: loading ? '#ccc' : '#7F56DA',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                border: 'none',
                fontSize: '14px'
              }}
            >
              {loading ? 'Creating...' : 'Create Permission'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePermissionModal;
